import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Accordion, AccordionSummary, AccordionDetails, Box, MenuItem, Select, FormLabel, Button, CircularProgress, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReportSearchBar from "../reportSearchBar/ReportSearchBar";
import EnergiesOverview from "./EnergiesOverview";
import { useSystemId, useReports } from "../../../hooks/useReduxSelectors";
import { useTranslation } from "react-i18next";
import { fetchReports } from "../../../ducks/reportsDuck";
import { getReportUrl } from "../../../constants/apiUrl";
import { getUniqueMeasurementUnitNames, getUniqueValueNames } from "./energiesUtilityFunctions";
import EnergiesReportFunctionButtonsRow from "./EnergiesReportFunctionButtonsRow";
import { bigTestDataEnergyDay } from "./testDataEnergyDay";
import { bigTestDataEnergyMonth } from "./testDataEnergyMonth";
import EnergyReportPrintVersion from "./EnergyReportPrintVersion";


function EnergiesReport({ energyReportType, systemName }) {

  const systemId = useSystemId();
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const reports = useReports();

  const energiesMonthReportData = reports?.EnergiesReportMonth?.entries;

  const energiesDayReportData = reports?.EnergiesReportDay?.entries;

  // const energiesMonthReportData = bigTestDataEnergyMonth; // for testing with big data
  // const energiesDayReportData = bigTestDataEnergyDay; // for testing with big data

  console.log("energiesMonthReportData: ", energiesMonthReportData);
  console.log("energiesDayReportData: ", energiesDayReportData);

  let reportData;
  if (energyReportType === "Month") {
    reportData = energiesMonthReportData;
  } else {
    reportData = energiesDayReportData;
  }

  const [uniqueValueNames, setUniqueValueNames] = useState([]);
  const [uniqueUnitNames, setUniqueUnitNames] = useState([]);

  // Update unique values when reportData or subReportGroupSelection changes
  useEffect(() => {
    if (reportData) {
      setUniqueValueNames(getUniqueValueNames(reportData, subReportGroupSelection));
      setUniqueUnitNames(getUniqueMeasurementUnitNames(reportData, subReportGroupSelection));
    }
  }, [reportData, subReportGroupSelection]);
  // let environment;
  // if (systemId !== "edge") {environment = "cloud"}

  const [loading, setLoading] = useState(true);
  // const [heatMapColor, setHeatMapColor] = useState("180, 35, 15");
  // const [highlightValues, setHighlightValues] = useState("high");  // highlighting "high" or "low" or "high/low" or "no" values
  const [heatmapOn, setHeatmapOn] = useState(true);
  const [piechartOn, setPiechartOn] = useState(true);
  const [filteredReportData, setFilteredReportData] = useState(null);
  const [subReportGroupSelection, setSubReportGroupSelection] = useState("");
  const [groupSelectionChoices, setGroupSelectionChoices] = useState([]);
  const [activeFilter, setActiveFilter] = useState(null); // Track active filter
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);

  console.log("subReportGroupSelection: ", subReportGroupSelection);
  console.log("groupSelectionChoices: ", groupSelectionChoices);
  console.log("reportData: ", reportData);

  const handleSubReportGroupSelection = (groupName) => {
    setSubReportGroupSelection(groupName);
  }


  const togglePieChart = () => {
    setPiechartOn(!piechartOn);
  };

  const handleFilterChange = (valueName) => {
    // if user has selected filter "Show all", use unfiltered reportData
    if (valueName === "all") {
      setFilteredReportData(reportData);
      setActiveFilter(null); // Clear active filter
    } else {
      const filterValues = valueName.split("|"); // e.g: "valueName|L1TotalEa" turn into ["valueName", "L1TotalEa"]
      let filteredData;

      if (filterValues[0] === "valueName") {
        filteredData = reportData.map((report) => {
          const updatedSubReports = { ...report.SubReports };

          // Filter only the selected group
          if (subReportGroupSelection && updatedSubReports[subReportGroupSelection]) {
            const filteredGroupValues = updatedSubReports[subReportGroupSelection].filter(
              (value) => value.ValueName === filterValues[1]
            );
            updatedSubReports[subReportGroupSelection] = filteredGroupValues;
          }

          return { ...report, SubReports: updatedSubReports };
        });
        setActiveFilter({ type: "valueName", value: filterValues[1] });
      } else if (filterValues[0] === "unitName") {
        filteredData = reportData.map((report) => {
          const updatedSubReports = { ...report.SubReports };

          // Filter only the selected group
          if (subReportGroupSelection && updatedSubReports[subReportGroupSelection]) {
            const filteredGroupValues = updatedSubReports[subReportGroupSelection].filter(
              (value) => value.Unit === filterValues[1]
            );
            updatedSubReports[subReportGroupSelection] = filteredGroupValues;
          }

          return { ...report, SubReports: updatedSubReports };
        });
        setActiveFilter({ type: "unitName", value: filterValues[1] });
      }
      setFilteredReportData(filteredData);
    }
  };


  const toggleHeatmap = () => {
    setHeatmapOn(!heatmapOn);
  }

  const openPrintPage = () => {
    setIsPrintDialogOpen(true);
  };

  const handlePrintDialogClose = () => {
    setIsPrintDialogOpen(false);
  };

  // const swapHeatMapHighligthValues = () => {
  //   if (highlightValues === "high") {
  //     setHighlightValues("low");
  //   } else if (highlightValues === "low") {
  //     setHighlightValues("high/low");
  //   } else if (highlightValues === "high/low") {
  //     setHighlightValues("no");
  //   } else {
  //     setHighlightValues("high");
  //   }
  // };

  // let heatmapButtonText = "";
  // if (highlightValues === "high") {
  //   heatmapButtonText = "Highlight High Values";
  // } else if (highlightValues === "low") {
  //   heatmapButtonText = "Highlight Low Values";
  // } else if (highlightValues === "high/low") {
  //   heatmapButtonText = "Highlight High & Low Values";
  // } else if (highlightValues === "no") {
  //   heatmapButtonText = "Heatmap Off";
  // }

  const fetchOnFirstLoad = useCallback(() => {
    let energyReportNameForFetch;
    if (energyReportType === "Month") {
      energyReportNameForFetch = "MonthHeatMap";
    } else {  // "Day"
      energyReportNameForFetch = "DayHeatMap";
    }
    const args = {
      ReportName: energyReportNameForFetch,
      Count: 1,
    };
    dispatch(fetchReports({ systemId, url: getReportUrl, type: "EnergiesReport", args })).then(() => {
      setLoading(false);
    });
  }, [dispatch]);

  // This makes sure fetched reports are shown even when user has active filters.
  useEffect(() => {
    setFilteredReportData(reportData);
  }, [reportData]);

  // Reset filtered data when group selection changes
  useEffect(() => {
    if (reportData) {
      setFilteredReportData(reportData);
      setActiveFilter(null); // Clear active filter when group changes
    }
  }, [subReportGroupSelection]);


  // in report data there are multiple reports, each report has "subReports" and those reports are then grouped.
  useEffect(() => {
    if (reportData && reportData.length > 0) {
      // Get the first report's SubReports keys and set it as the default selection
      const firstReportSubReports = reportData[0].SubReports;
      if (firstReportSubReports) {
        const firstGroupName = Object.keys(firstReportSubReports)[0];
        setSubReportGroupSelection(firstGroupName);
      }
      // Get all unique group names from the first report's SubReports
      const groupNames = Object.keys(firstReportSubReports).map(groupName => ({ groupName }));
      setGroupSelectionChoices(groupNames);
    }
  }, [reportData]);

  // const subReportGroups = Object.entries(singleReport.SubReports);
  // const subReportGroupsFormatted = subReportGroups.map(([groupName, measurements]) => ({ groupName, measurements }));

  return (
    <React.Fragment>
      <EnergyReportPrintVersion
        isOpen={isPrintDialogOpen}
        onClose={handlePrintDialogClose}
        reportData={filteredReportData || reportData}
        systemName={systemName}
        loading={loading}
        t={t}
        reportType={energyReportType}
        subReportGroupSelection={subReportGroupSelection}
        heatmapOn={heatmapOn}
        piechartOn={piechartOn}
        togglePieChart={togglePieChart}
        toggleHeatmap={toggleHeatmap}
      />
      <Accordion
        disableGutters
        onChange={(e, expanded) => { if (expanded) fetchOnFirstLoad(); }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "common.white" }} />}
          sx={{ bgcolor: "primary.main", color: "common.white", m: "1px 0 0 0" }}
        >
          <Typography sx={{ flex: 1, fontWeight: "bold" }}>{`Energies Report ${energyReportType}`}</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ bgcolor: "background.light", p: "10px 0" }}>
          <ReportSearchBar reportName="EnergiesReport" energyReportType={energyReportType} />
          {loading && <CircularProgress size={24} />}
          <Button variant="contained" color="success"
            onClick={openPrintPage}
            style={{ marginLeft: "10px", marginBottom: "10px" }}>
            Download PDF
          </Button>
          {Object.keys(reportData).length > 0 &&
            <Box className="energies-report" sx={{ padding: 2 }}>
              <EnergiesReportFunctionButtonsRow
                handleFilterChange={handleFilterChange}
                piechartOn={piechartOn}
                togglePieChart={togglePieChart}
                uniqueValueNames={uniqueValueNames}
                uniqueUnitNames={uniqueUnitNames}
                toggleHeatmap={toggleHeatmap}
                heatmapOn={heatmapOn}
                activeFilter={activeFilter}
                subReportGroupSelection={subReportGroupSelection}
                handleSubReportGroupSelection={handleSubReportGroupSelection}
                groupSelectionChoices={groupSelectionChoices}
              />

              <EnergiesOverview
                // environment={environment}
                piechartOn={piechartOn}
                heatmapOn={heatmapOn}
                reportData={filteredReportData || reportData}
                reportType={energyReportType}
                subReportGroupSelection={subReportGroupSelection}
                handleSubReportGroupSelection={handleSubReportGroupSelection}
              />
            </Box>
          }
        </AccordionDetails>
      </Accordion>
    </React.Fragment>
  );
}
export default EnergiesReport;